import { useMemo, useCallback } from "react";
import { useThemedLabels } from "../../../hooks/use-themed-labels";
import { useAuth } from "../../../../lib/hooks/use-auth";
import type { Command, CommandContext } from "../types";

/**
 * Workforce commands — Sprint 23 (S23.23–S23.26).
 *
 * The themed "Hire" entry point, an "Open Workforce" jump, three "Filter to …"
 * deep-links into the route hash, and the three sort affordances (persisted in
 * localStorage so the Workforce route picks them up on next mount). All labels
 * flow through {@link useThemedLabels}, so this single file re-skins itself when
 * the workspace theme changes.
 *
 * Sprint 23.5 — "Hire" navigates to the conversational composition wizard
 * at `/hire`. The previous form-based NewAgentModal seam is gone; the wizard
 * is the only hire surface. Workforce-specific keywords surface the themed
 * label first when the user types "hire" / "recruit" / "bring on" / etc.
 */

type SortMode = "recent" | "alphabetical" | "hierarchy";
const SORT_LS_PREFIX = "kuvio_workforce_sort_";

function saveSort(workspaceId: string | null, sort: SortMode): void {
  if (!workspaceId) return;
  try {
    localStorage.setItem(`${SORT_LS_PREFIX}${workspaceId}`, sort);
  } catch {
    /* ignore */
  }
}

export function useWorkforceCommands(ctx: CommandContext): Command[] {
  const { navigate } = ctx;
  const labels = useThemedLabels();
  const { currentWorkspaceId } = useAuth();

  const applySort = useCallback(
    (sort: SortMode) => {
      saveSort(currentWorkspaceId, sort);
      navigate(`/workforce#sort=${sort}`);
    },
    [navigate, currentWorkspaceId],
  );

  return useMemo<Command[]>(() => {
    const collective = labels.collective; // "Workforce" / "Crew" / "Studio" / …
    const collectiveLower = labels.collectiveLower;

    const cmds: Command[] = [];

    // S23.23 / S23.5.40 — themed Hire navigates to the wizard at /hire.
    cmds.push({
      id: "workforce:hire",
      label: labels.addAction,
      icon: "user-plus",
      kind: "create",
      subtitle: labels.addActionDesc,
      keywords: ["hire", "recruit", "bring on", "spin up", "add", "new agent", "create"],
      execute: () => navigate("/hire"),
    });

    // S23.24 — open the Workforce route.
    cmds.push({
      id: "workforce:open",
      label: `Open ${collective}`,
      icon: "briefcase",
      kind: "nav",
      subtitle: "Workspace",
      keywords: ["workforce", "crew", "studio", "lab", "team", "agents", "go", "open"],
      execute: () => navigate("/workforce"),
    });

    // S23.25 — three filter deep-links. URL hash drives the chip selection.
    const filters: { key: "working" | "awaiting" | "available"; label: string; icon: string }[] = [
      { key: "working", label: labels.workingCount, icon: "player-play" },
      { key: "awaiting", label: labels.awaitingCount, icon: "hourglass" },
      { key: "available", label: labels.availableCount, icon: "circle-check" },
    ];
    for (const f of filters) {
      cmds.push({
        id: `workforce:filter:${f.key}`,
        label: `Filter ${collectiveLower} to ${f.label}`,
        icon: f.icon,
        kind: "action",
        subtitle: collective,
        keywords: ["filter", "show", "only", f.key, f.label, collectiveLower],
        execute: () => navigate(`/workforce#filter=${f.key}`),
      });
    }

    // S23.26 — three sort affordances (also persisted to localStorage as a
    // per-workspace default).
    const sorts: { key: SortMode; label: string; icon: string; keywords: string[] }[] = [
      { key: "recent", label: "Recently active", icon: "clock", keywords: ["sort", "recent", "active", "now"] },
      { key: "alphabetical", label: "Alphabetical", icon: "sort-ascending-letters", keywords: ["sort", "alphabetical", "name", "a to z"] },
      { key: "hierarchy", label: "By hierarchy", icon: "hierarchy", keywords: ["sort", "tree", "parent", "hierarchy"] },
    ];
    for (const s of sorts) {
      cmds.push({
        id: `workforce:sort:${s.key}`,
        label: `Sort ${collectiveLower} — ${s.label}`,
        icon: s.icon,
        kind: "action",
        subtitle: collective,
        keywords: s.keywords,
        execute: () => applySort(s.key),
      });
    }

    return cmds;
  }, [labels, navigate, applySort]);
}
